import "./index.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "@/components/ui/sonner";
import { ApiError } from "@/api/custom-fetch";
import { setupNavigationHistoryTracker } from "@/lib/navigation-history";
import { initTelemetry, registerTelemetryFlush } from "@/lib/telemetry";

// Start tracing before the router mounts so the first navigation's API calls
// already have a tracer to hang off.
initTelemetry();
registerTelemetryFlush();

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({ routeTree });

// Subscribe to navigation events before any route renders so the
// detail-page back button can tell whether `router.history.back()` will
// stay inside the app.
setupNavigationHistoryTracker(router);

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Create a client
// Don't retry on auth / not-found errors — they won't change on a second
// attempt and just duplicate console noise + delay the UI's empty/forbidden
// state. Everything else gets one retry (network blip, cold start, etc).
const NO_RETRY_STATUSES = new Set([401, 403, 404]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: (failureCount, error) => {
        if (error instanceof ApiError && NO_RETRY_STATUSES.has(error.status)) {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        if (error instanceof ApiError && NO_RETRY_STATUSES.has(error.status)) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

// Render the app
const rootElement = document.getElementById("root");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
        <Toaster position="top-right" />
      </QueryClientProvider>
    </StrictMode>,
  );
}
